import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  TaskyButton,
  TaskyFormGroup,
  TaskyInput,
  TaskySwitch,
  TaskyTextarea,
} from '../tasky';
import { AdminVideoTemplate, VideoTemplateCreateRequest, VideoTemplateUpdateRequest } from '../../services/api/adminVideoTemplateService';
import VideoTemplateSampleField, { VideoTemplateSampleValue } from './VideoTemplateSampleField';
import VideoTemplateThumbnailField from './VideoTemplateThumbnailField';

const EMPTY_SAMPLE: VideoTemplateSampleValue = { videoKey: null, posterKey: null, videoUrl: null, posterUrl: null };

interface VideoTemplateFormModalProps {
  isOpen: boolean;
  videoTemplate: AdminVideoTemplate | null;
  onSubmit: (data: VideoTemplateCreateRequest | VideoTemplateUpdateRequest) => void;
  onClose: () => void;
  isLoading: boolean;
}

const VideoTemplateFormModal: React.FC<VideoTemplateFormModalProps> = ({
  isOpen,
  videoTemplate,
  onSubmit,
  onClose,
  isLoading
}) => {
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    icon: '',
    description: '',
    prompt_template: '',
    gradient: '',
    thumbnail_url: '',
    sort_order: 0,
    is_active: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sample, setSample] = useState<VideoTemplateSampleValue>(EMPTY_SAMPLE);
  const [sampleUploading, setSampleUploading] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  useEffect(() => {
    if (videoTemplate) {
      setFormData({
        category: videoTemplate.category,
        name: videoTemplate.name,
        icon: videoTemplate.icon || '',
        description: videoTemplate.description || '',
        prompt_template: videoTemplate.prompt_template,
        gradient: videoTemplate.gradient || '',
        thumbnail_url: videoTemplate.thumbnail_url || '',
        sort_order: videoTemplate.sort_order,
        is_active: videoTemplate.is_active
      });
      setSample({
        videoKey: videoTemplate.sample_video_key ?? null,
        posterKey: videoTemplate.sample_poster_key ?? null,
        videoUrl: videoTemplate.sample_video_url ?? null,
        posterUrl: videoTemplate.sample_poster_url ?? null,
      });
    } else {
      setFormData({
        category: '',
        name: '',
        icon: '',
        description: '',
        prompt_template: '',
        gradient: '',
        thumbnail_url: '',
        sort_order: 0,
        is_active: true
      });
      setSample(EMPTY_SAMPLE);
    }
    setErrors({});
  }, [videoTemplate, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.category.trim()) {
      newErrors.category = 'カテゴリは必須です';
    } else if (formData.category.length > 255) {
      newErrors.category = 'カテゴリは255文字以内で入力してください';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'テンプレート名は必須です';
    } else if (formData.name.length > 255) {
      newErrors.name = 'テンプレート名は255文字以内で入力してください';
    }

    if (!formData.prompt_template.trim()) {
      newErrors.prompt_template = 'プロンプトテンプレートは必須です';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = '説明は1000文字以内で入力してください';
    }

    if (formData.sort_order < 0) {
      newErrors.sort_order = '並び順は0以上の数値を入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData: VideoTemplateCreateRequest | VideoTemplateUpdateRequest = {
      category: formData.category.trim(),
      name: formData.name.trim(),
      icon: formData.icon.trim() || undefined,
      description: formData.description.trim() || undefined,
      prompt_template: formData.prompt_template.trim(),
      gradient: formData.gradient.trim() || undefined,
      // 外した場合も null を送って列を空にする
      thumbnail_url: formData.thumbnail_url.trim() || null,
      // 外した場合も null を送って列を空にする
      sample_video_key: sample.videoKey,
      sample_poster_key: sample.videoKey ? sample.posterKey : null,
      sort_order: formData.sort_order,
      is_active: formData.is_active
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-tasky-lg w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-card">
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-border shrink-0">
          <h5 className="text-lg font-semibold text-dark m-0">
            {videoTemplate ? '動画テンプレート編集' : '動画テンプレート新規作成'}
          </h5>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            aria-label="閉じる"
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="px-6 py-4 space-y-4 overflow-y-auto">
            {/* Category */}
            <TaskyFormGroup
              label="カテゴリ"
              required
              htmlFor="vt-category"
              error={errors.category}
            >
              <TaskyInput
                type="text"
                id="vt-category"
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                placeholder="カテゴリ名を入力してください（例: commercial, social_media）"
                disabled={isLoading}
                maxLength={255}
                hasError={!!errors.category}
              />
            </TaskyFormGroup>

            {/* Name */}
            <TaskyFormGroup
              label="テンプレート名"
              required
              htmlFor="vt-name"
              error={errors.name}
            >
              <TaskyInput
                type="text"
                id="vt-name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="テンプレート名を入力してください"
                disabled={isLoading}
                maxLength={255}
                hasError={!!errors.name}
              />
            </TaskyFormGroup>

            {/* Icon */}
            <TaskyFormGroup
              label="アイコン"
              htmlFor="vt-icon"
              helper="テンプレート一覧で表示される絵文字アイコン"
            >
              <TaskyInput
                type="text"
                id="vt-icon"
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                placeholder="絵文字を入力（任意）"
                disabled={isLoading}
              />
            </TaskyFormGroup>

            {/* Description */}
            <TaskyFormGroup
              label="説明"
              htmlFor="vt-description"
              error={errors.description}
              helper={`${formData.description.length}/1000文字`}
            >
              <TaskyInput
                type="text"
                id="vt-description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="簡単な説明を入力（任意）"
                disabled={isLoading}
                maxLength={1000}
                hasError={!!errors.description}
              />
            </TaskyFormGroup>

            {/* Prompt Template */}
            <TaskyFormGroup
              label="プロンプトテンプレート"
              required
              htmlFor="vt-prompt_template"
              error={errors.prompt_template}
              helper="動画生成AIに送信されるプロンプトのテンプレートです"
            >
              <TaskyTextarea
                id="vt-prompt_template"
                value={formData.prompt_template}
                onChange={(e) => handleInputChange('prompt_template', e.target.value)}
                placeholder="動画生成に使用するプロンプトテンプレートを入力してください"
                disabled={isLoading}
                rows={6}
                hasError={!!errors.prompt_template}
              />
            </TaskyFormGroup>

            {/* Gradient */}
            <TaskyFormGroup
              label="グラデーション"
              htmlFor="vt-gradient"
              helper="テンプレートカードの背景に使用されるCSSグラデーション"
            >
              <TaskyInput
                type="text"
                id="vt-gradient"
                value={formData.gradient}
                onChange={(e) => handleInputChange('gradient', e.target.value)}
                placeholder="CSSグラデーション（例: linear-gradient(135deg, #667eea 0%, #764ba2 100%)）"
                disabled={isLoading}
              />
              {formData.gradient && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-1">プレビュー:</p>
                  <div
                    className="w-full h-10 rounded-tasky-xs border border-warm-border"
                    style={{ background: formData.gradient }}
                  />
                </div>
              )}
            </TaskyFormGroup>

            <VideoTemplateThumbnailField
              value={formData.thumbnail_url}
              onChange={(url) => handleInputChange('thumbnail_url', url)}
              onUploadingChange={setThumbnailUploading}
              disabled={isLoading}
            />

            <VideoTemplateSampleField
              value={sample}
              onChange={setSample}
              onUploadingChange={setSampleUploading}
              disabled={isLoading}
            />

            {/* Sort Order */}
            <TaskyFormGroup
              label="並び順"
              htmlFor="vt-sort_order"
              error={errors.sort_order}
              helper="数値が小さいほど上位に表示されます"
            >
              <TaskyInput
                type="number"
                id="vt-sort_order"
                value={formData.sort_order}
                onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 0)}
                min="0"
                disabled={isLoading}
                hasError={!!errors.sort_order}
              />
            </TaskyFormGroup>

            {/* Is Active */}
            <TaskyFormGroup helper="無効にするとユーザーには表示されません">
              <TaskySwitch
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                disabled={isLoading}
                label={<span className="font-medium">有効</span>}
              />
            </TaskyFormGroup>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 bg-warm-bg border-t border-warm-border shrink-0">
            <TaskyButton
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={isLoading}
            >
              キャンセル
            </TaskyButton>
            <TaskyButton type="submit" size="sm" isLoading={isLoading} disabled={sampleUploading || thumbnailUploading}>
              {isLoading
                ? (videoTemplate ? '更新中...' : '作成中...')
                : (videoTemplate ? '更新' : '作成')}
            </TaskyButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VideoTemplateFormModal;
